import React, { useEffect, useState, useMemo, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import "./viewHomeCategory.css";
import { useDispatch, useSelector } from "react-redux";
import {
    AddHomeCategory,
    UpdateHomeCategory,
    ViewHomeCategory,
} from "../../Redux/Actions/homeCategoryAction";
import AddImage from "../../Global Functions/addImage";
import toast from "react-hot-toast";

const DEFAULT_NEW_CATEGORY = {
    home_gif: "",
    categories: [
        { type: "", image: "", subtypes: [""] }
    ],
};

const MediaPreview = ({ src, className }) => {
    if (!src) return <div className="hcv-no-image">No GIF / Video uploaded</div>;
    const isVideo = /\.(mp4|webm|mov|ogg)(\?|$)/i.test(src);
    return isVideo
        ? <video src={src} className={className} autoPlay loop muted playsInline />
        : <img src={src} alt="Home GIF" className={className} />;
};

const HomeCategoryView = () => {
    const dispatch = useDispatch();
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [addMode, setAddMode] = useState(false);
    const [addData, setAddData] = useState(DEFAULT_NEW_CATEGORY);
    const [uploadingGif, setUploadingGif] = useState(false);
    const [uploadingCatImage, setUploadingCatImage] = useState({});

    const { admin } = useSelector((state) => state.admin || {});
    const { category, loading } = useSelector((state) => state.homeCategory || {});

    const { canEdit } = useMemo(() => {
        const codes = admin?.data?.access_code || [];
        return { canEdit: Array.isArray(codes) && codes.includes("edit") };
    }, [admin]);

    useEffect(() => {
        dispatch(ViewHomeCategory());
    }, [dispatch]);

    const config = (Array.isArray(category) && category.length > 0) ? category[0] : null;
    const isDataNull = !loading && config === null;

    // ─── GIF Upload ───────────────────────────────────────
    const handleGifUpload = useCallback(async (file, isAdd = false) => {
        try {
            setUploadingGif(true);
            const url = await AddImage(file);
            if (isAdd) {
                setAddData((prev) => ({ ...prev, home_gif: url }));
            } else {
                setEditData((prev) => ({ ...prev, home_gif: url }));
            }
            toast.success("GIF uploaded!");
        } catch {
            toast.error("GIF upload failed!");
        } finally {
            setUploadingGif(false);
        }
    }, []);

    // ─── Category Image Upload ────────────────────────────
    const handleCatImageUpload = useCallback(async (file, index, isAdd = false) => {
        try {
            setUploadingCatImage((prev) => ({ ...prev, [index]: true }));
            const url = await AddImage(file);
            if (isAdd) {
                setAddData((prev) => {
                    const cats = [...prev.categories];
                    cats[index] = { ...cats[index], image: url };
                    return { ...prev, categories: cats };
                });
            } else {
                setEditData((prev) => {
                    const cats = [...prev.categories];
                    cats[index] = { ...cats[index], image: url };
                    return { ...prev, categories: cats };
                });
            }
            toast.success("Image uploaded!");
        } catch {
            toast.error("Image upload failed!");
        } finally {
            setUploadingCatImage((prev) => ({ ...prev, [index]: false }));
        }
    }, []);

    // ─── Add Handlers ─────────────────────────────────────
    const handleAddCategoryItem = useCallback(() => {
        setAddData((prev) => ({
            ...prev,
            categories: [...prev.categories, { type: "", image: "", subtypes: [""] }],
        }));
    }, []);

    const handleRemoveCategoryItem = useCallback((index) => {
        setAddData((prev) => {
            const cats = [...prev.categories];
            cats.splice(index, 1);
            return { ...prev, categories: cats };
        });
    }, []);

    const handleAddCategoryChange = useCallback((index, field, value) => {
        setAddData((prev) => {
            const cats = [...prev.categories];
            cats[index] = { ...cats[index], [field]: value };
            return { ...prev, categories: cats };
        });
    }, []);

    const handleAddSubtypeChange = useCallback((catIndex, subIndex, value) => {
        setAddData((prev) => {
            const cats = [...prev.categories];
            const subtypes = [...(cats[catIndex].subtypes || [])];
            subtypes[subIndex] = value;
            cats[catIndex] = { ...cats[catIndex], subtypes };
            return { ...prev, categories: cats };
        });
    }, []);

    const handleAddSubtype = useCallback((catIndex) => {
        setAddData((prev) => {
            const cats = [...prev.categories];
            cats[catIndex] = {
                ...cats[catIndex],
                subtypes: [...(cats[catIndex].subtypes || []), ""],
            };
            return { ...prev, categories: cats };
        });
    }, []);

    const handleRemoveSubtype = useCallback((catIndex, subIndex) => {
        setAddData((prev) => {
            const cats = [...prev.categories];
            const subtypes = [...(cats[catIndex].subtypes || [])];
            subtypes.splice(subIndex, 1);
            cats[catIndex] = { ...cats[catIndex], subtypes };
            return { ...prev, categories: cats };
        });
    }, []);

    const handleCreate = useCallback(async () => {
        try {
            const payload = {
                user_id: admin?.data?._id,
                home_gif: addData.home_gif,
                categories: addData.categories,
            };
            await dispatch(AddHomeCategory(payload));
            toast.success("Home category created!");
            dispatch(ViewHomeCategory());
            setAddMode(false);
            setAddData(DEFAULT_NEW_CATEGORY);
        } catch (error) {
            toast.error(error?.message || "Failed to create");
        }
    }, [dispatch, addData, admin?.data?._id]);

    // ─── Edit Handlers ────────────────────────────────────
    const handleEditClick = useCallback(() => {
        if (config) {
            setEditData({
                ...config,
                categories: config.categories.map((c) => ({
                    ...c,
                    subtypes: [...(c.subtypes || [])],
                })),
            });
            setEditing(true);
        }
    }, [config]);

    const handleEditCategoryChange = useCallback((index, field, value) => {
        setEditData((prev) => {
            const cats = [...prev.categories];
            cats[index] = { ...cats[index], [field]: value };
            return { ...prev, categories: cats };
        });
    }, []);

    const handleEditSubtypeChange = useCallback((catIndex, subIndex, value) => {
        setEditData((prev) => {
            const cats = [...prev.categories];
            const subtypes = [...(cats[catIndex].subtypes || [])];
            subtypes[subIndex] = value;
            cats[catIndex] = { ...cats[catIndex], subtypes };
            return { ...prev, categories: cats };
        });
    }, []);

    const handleEditAddSubtype = useCallback((catIndex) => {
        setEditData((prev) => {
            const cats = [...prev.categories];
            cats[catIndex] = {
                ...cats[catIndex],
                subtypes: [...(cats[catIndex].subtypes || []), ""],
            };
            return { ...prev, categories: cats };
        });
    }, []);

    const handleEditRemoveSubtype = useCallback((catIndex, subIndex) => {
        setEditData((prev) => {
            const cats = [...prev.categories];
            const subtypes = [...(cats[catIndex].subtypes || [])];
            subtypes.splice(subIndex, 1);
            cats[catIndex] = { ...cats[catIndex], subtypes };
            return { ...prev, categories: cats };
        });
    }, []);

    const handleAddEditCategoryItem = useCallback(() => {
        setEditData((prev) => ({
            ...prev,
            categories: [...prev.categories, { type: "", image: "", subtypes: [""] }],
        }));
    }, []);

    const handleRemoveEditCategoryItem = useCallback((index) => {
        setEditData((prev) => {
            const cats = [...prev.categories];
            cats.splice(index, 1);
            return { ...prev, categories: cats };
        });
    }, []);

    const handleSave = useCallback(() => {
        const payload = {
            user_id: admin?.data?._id,
            home_gif: editData.home_gif,
            categories: editData.categories,
        };
        dispatch(UpdateHomeCategory(payload))
            .then(() => {
                toast.success("Updated successfully!");
                setEditing(false);
                dispatch(ViewHomeCategory());
            })
            .catch((error) => {
                toast.error(error?.message || "Failed to update");
            });
    }, [dispatch, editData, admin?.data?._id]);

    // ─── Loading ──────────────────────────────────────────
    if (loading) {
        return (
            <MainLayout>
                <div className="hcv-page">
                    <div className="hcv-loading">
                        <div className="hcv-spinner" />
                        <span>Loading home category...</span>
                    </div>
                </div>
            </MainLayout>
        );
    }

    // ─── No Data — Add Mode ───────────────────────────────
    if (isDataNull) {
        return (
            <MainLayout>
                <div className="hcv-page">
                    <div className="hcv-header">
                        <div className="hcv-header-left">
                            <div className="hcv-breadcrumb">
                                <span>Settings</span>
                                <span className="hcv-breadcrumb-sep">/</span>
                                <span className="hcv-breadcrumb-current">Home Category</span>
                            </div>
                            <h1 className="hcv-title">Create Home Category</h1>
                            <p className="hcv-subtitle">
                                No configuration exists yet. Fill in the details below.
                            </p>
                        </div>
                        {addMode && (
                            <div className="hcv-header-actions">
                                <button className="hcv-btn hcv-btn-ghost"
                                    onClick={() => { setAddMode(false); setAddData(DEFAULT_NEW_CATEGORY); }}>
                                    Cancel
                                </button>
                                <button className="hcv-btn hcv-btn-primary" onClick={handleCreate}>
                                    <span className="hcv-btn-icon">✓</span> Create
                                </button>
                            </div>
                        )}
                    </div>

                    {!addMode ? (
                        <div className="hcv-section">
                            <div className="hcv-empty-state">
                                <div className="hcv-empty-icon">🖼</div>
                                <p>No home category found. Create one to get started.</p>
                                <button className="hcv-btn hcv-btn-primary" style={{ marginTop: 8 }}
                                    onClick={() => setAddMode(true)}>
                                    <span className="hcv-btn-icon">+</span> Add Category
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="hcv-edit-banner">
                                <span className="hcv-edit-dot" />
                                Fill in all fields and click <strong>Create</strong> to save.
                            </div>
                            <div className="hcv-content">
                                {/* GIF Upload */}
                                <div className="hcv-section">
                                    <div className="hcv-section-header">
                                        <div className="hcv-section-tag">Media</div>
                                        <h2 className="hcv-section-title">Home GIF / Video</h2>
                                    </div>
                                    <div className="hcv-section-body">
                                        <div className="hcv-gif-row">
                                            <MediaPreview src={addData.home_gif} className="hcv-gif-preview" />
                                            <label className="hcv-upload-btn">
                                                {uploadingGif ? "Uploading..." : "Upload GIF / Video"}
                                                <input type="file" accept="image/gif,video/*" hidden
                                                    onChange={(e) => e.target.files[0] && handleGifUpload(e.target.files[0], true)} />
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Categories */}
                                <div className="hcv-section">
                                    <div className="hcv-section-header">
                                        <div className="hcv-section-tag">Categories</div>
                                        <h2 className="hcv-section-title">Category Items</h2>
                                    </div>

                                    {/* ── 2-per-row grid ── */}
                                    <div className="hcv-categories-grid">
                                        {addData.categories.map((cat, index) => (
                                            <div key={index} className="hcv-cat-card hcv-cat-card--editing">
                                                <div className="hcv-cat-index">
                                                    #{index + 1}
                                                    {addData.categories.length > 1 && (
                                                        <button className="hcv-remove-btn"
                                                            onClick={() => handleRemoveCategoryItem(index)}>×</button>
                                                    )}
                                                </div>
                                                <div className="hcv-cat-grid">
                                                    <div className="hcv-field-group">
                                                        <label className="hcv-field-label">Type</label>
                                                        <input className="hcv-input" placeholder="e.g. Fresh Properties"
                                                            value={cat.type}
                                                            onChange={(e) => handleAddCategoryChange(index, "type", e.target.value)} />
                                                    </div>
                                                    <div className="hcv-field-group">
                                                        <label className="hcv-field-label">Image</label>
                                                        <div className="hcv-img-upload-row">
                                                            {cat.image && (
                                                                <img src={cat.image} alt="cat" className="hcv-cat-thumb" />
                                                            )}
                                                            <label className="hcv-upload-btn hcv-upload-btn--sm">
                                                                {uploadingCatImage[index] ? "Uploading..." : "Upload"}
                                                                <input type="file" accept="image/*" hidden
                                                                    onChange={(e) => e.target.files[0] && handleCatImageUpload(e.target.files[0], index, true)} />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="hcv-subtypes-wrap">
                                                    <label className="hcv-field-label">Subtypes</label>
                                                    <div className="hcv-subtypes-list">
                                                        {(cat.subtypes || []).map((sub, si) => (
                                                            <div key={si} className="hcv-subtype-row">
                                                                <input className="hcv-input hcv-input--sub"
                                                                    placeholder={`Subtype ${si + 1}`}
                                                                    value={sub}
                                                                    onChange={(e) => handleAddSubtypeChange(index, si, e.target.value)} />
                                                                {cat.subtypes.length > 1 && (
                                                                    <button className="hcv-sub-remove"
                                                                        onClick={() => handleRemoveSubtype(index, si)}>×</button>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <button className="hcv-btn hcv-btn-ghost hcv-sub-add-btn"
                                                        onClick={() => handleAddSubtype(index)}>
                                                        + Add Subtype
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="hcv-add-cat-wrapper">
                                        <button className="hcv-btn hcv-btn-secondary"
                                            onClick={handleAddCategoryItem}>
                                            <span className="hcv-btn-icon">+</span> Add Category Item
                                        </button>
                                    </div>
                                </div>

                                <div className="hcv-footer-actions">
                                    <button className="hcv-btn hcv-btn-ghost"
                                        onClick={() => { setAddMode(false); setAddData(DEFAULT_NEW_CATEGORY); }}>
                                        Cancel
                                    </button>
                                    <button className="hcv-btn hcv-btn-primary" onClick={handleCreate}>
                                        <span className="hcv-btn-icon">✓</span> Create
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </MainLayout>
        );
    }

    // ─── View / Edit ──────────────────────────────────────
    const categories = editing ? editData.categories || [] : config.categories || [];

    return (
        <MainLayout>
            <div className="hcv-page">
                <div className="hcv-header">
                    <div className="hcv-header-left">
                        <div className="hcv-breadcrumb">
                            <span>Settings</span>
                            <span className="hcv-breadcrumb-sep">/</span>
                            <span className="hcv-breadcrumb-current">Home Category</span>
                        </div>
                        <h1 className="hcv-title">Home Category</h1>
                        <p className="hcv-subtitle">Manage home category items and GIF</p>
                    </div>
                    <div className="hcv-header-actions">
                        {editing ? (
                            <>
                                <button className="hcv-btn hcv-btn-ghost" onClick={() => setEditing(false)}>
                                    Cancel
                                </button>
                                <button className="hcv-btn hcv-btn-primary" onClick={handleSave}>
                                    <span className="hcv-btn-icon">✓</span> Save Changes
                                </button>
                            </>
                        ) : (
                            canEdit && (
                                <button className="hcv-btn hcv-btn-secondary" onClick={handleEditClick}>
                                    <span className="hcv-btn-icon">✎</span> Edit
                                </button>
                            )
                        )}
                    </div>
                </div>

                {editing && (
                    <div className="hcv-edit-banner">
                        <span className="hcv-edit-dot" />
                        You are in edit mode — click <strong>Save Changes</strong> to apply.
                    </div>
                )}

                <div className="hcv-content">
                    {/* GIF Section */}
                    <div className="hcv-section">
                        <div className="hcv-section-header">
                            <div className="hcv-section-tag">Media</div>
                            <h2 className="hcv-section-title">Home GIF / Video</h2>
                        </div>
                        <div className="hcv-section-body">
                            <div className="hcv-gif-row">
                                <MediaPreview
                                    src={editing ? editData.home_gif : config.home_gif}
                                    className="hcv-gif-preview"
                                />
                                {editing && (
                                    <label className="hcv-upload-btn">
                                        {uploadingGif ? "Uploading..." : "Change GIF / Video"}
                                        <input type="file" accept="image/gif,video/*" hidden
                                            onChange={(e) => e.target.files[0] && handleGifUpload(e.target.files[0], false)} />
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Categories Section */}
                    <div className="hcv-section">
                        <div className="hcv-section-header">
                            <div className="hcv-section-tag">Categories</div>
                            <h2 className="hcv-section-title">Category Items</h2>
                        </div>

                        {/* ── 2-per-row grid ── */}
                        <div className="hcv-categories-grid">
                            {categories.map((cat, index) => (
                                <div key={cat._id || index}
                                    className={`hcv-cat-card ${editing ? "hcv-cat-card--editing" : ""}`}>
                                    <div className="hcv-cat-index">
                                        {editing ? `${index + 1}` : `#${index + 1}`}
                                        {editing && categories.length > 1 && (
                                            <button className="hcv-remove-btn"
                                                onClick={() => handleRemoveEditCategoryItem(index)}>×</button>
                                        )}
                                    </div>

                                    <div className="hcv-cat-grid">
                                        <div className="hcv-field-group">
                                            <label className="hcv-field-label">Type</label>
                                            {editing ? (
                                                <input className="hcv-input"
                                                    value={editData.categories?.[index]?.type ?? cat.type ?? ""}
                                                    onChange={(e) => handleEditCategoryChange(index, "type", e.target.value)} />
                                            ) : (
                                                <span className="hcv-field-value hcv-type-badge">{cat.type}</span>
                                            )}
                                        </div>

                                        <div className="hcv-field-group">
                                            <label className="hcv-field-label">Image</label>
                                            {editing ? (
                                                <div className="hcv-img-upload-row">
                                                    {editData.categories?.[index]?.image && (
                                                        <img src={editData.categories[index].image}
                                                            alt="cat" className="hcv-cat-thumb" />
                                                    )}
                                                    <label className="hcv-upload-btn hcv-upload-btn--sm">
                                                        {uploadingCatImage[index] ? "Uploading..." : "Change"}
                                                        <input type="file" accept="image/*" hidden
                                                            onChange={(e) => e.target.files[0] && handleCatImageUpload(e.target.files[0], index, false)} />
                                                    </label>
                                                </div>
                                            ) : (
                                                cat.image ? (
                                                    <img src={cat.image} alt={cat.type} className="hcv-cat-thumb" />
                                                ) : (
                                                    <span className="hcv-no-image">No image</span>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    <div className="hcv-subtypes-wrap">
                                        <label className="hcv-field-label">Subtypes</label>
                                        {editing ? (
                                            <>
                                                <div className="hcv-subtypes-list">
                                                    {(editData.categories?.[index]?.subtypes || []).map((sub, si) => (
                                                        <div key={si} className="hcv-subtype-row">
                                                            <input className="hcv-input hcv-input--sub"
                                                                value={sub}
                                                                onChange={(e) => handleEditSubtypeChange(index, si, e.target.value)} />
                                                            {editData.categories[index].subtypes.length > 1 && (
                                                                <button className="hcv-sub-remove"
                                                                    onClick={() => handleEditRemoveSubtype(index, si)}>×</button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <button className="hcv-btn hcv-btn-ghost hcv-sub-add-btn"
                                                    onClick={() => handleEditAddSubtype(index)}>
                                                    + Add Subtype
                                                </button>
                                            </>
                                        ) : (
                                            <div className="hcv-subtypes-tags">
                                                {(cat.subtypes || []).map((sub, si) => (
                                                    <span key={si} className="hcv-subtype-tag">{sub}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {editing && (
                            <div className="hcv-add-cat-wrapper">
                                <button className="hcv-btn hcv-btn-secondary"
                                    onClick={handleAddEditCategoryItem}>
                                    <span className="hcv-btn-icon">+</span> Add Category Item
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default HomeCategoryView;